"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const app_1 = require("app");
const React = require("react");
const webexUI_1 = require("webexUI");
const FilterAutocomplete_1 = require("../FilterAutocomplete/FilterAutocomplete");
const FilterTextFormField_1 = require("../FilterTextFormField/FilterTextFormField");
const Forms_1 = require("../Forms");
const CountriesWithStates_1 = require("./CountriesWithStates");
const FilterItems_1 = require("./FilterItems");
const FilterTypings_1 = require("./FilterTypings");
const MosaicIcon_1 = require("Views/Shared/Components/MosaicIcon");
const buildCollapsedGroup = (filterGroupsOrder, filters, previous) => {
    const snapshot = previous ? Object.assign({}, previous) : {};
    if (!Array.isArray(filterGroupsOrder) || !filters) {
        return snapshot;
    }
    return filterGroupsOrder.reduce((accumulator, groupKey) => {
        var _a;
        if (!(groupKey in accumulator)) {
            const filter = filters[groupKey];
            accumulator[groupKey] = (_a = filter === null || filter === void 0 ? void 0 : filter.isCollapsed) !== null && _a !== void 0 ? _a : false;
        }
        return accumulator;
    }, snapshot);
};
const getSelectedFilterByType = (filters, selectedFilters, typeAlias) => {
    const allGroups = (0, app_1.fromObjectToArray)(filters);
    const matchingGroup = allGroups.find(group => group.typeAlias === typeAlias);
    if (!matchingGroup) {
        return null;
    }
    const values = selectedFilters[matchingGroup.value];
    return (values === null || values === void 0 ? void 0 : values.length) ? values[0] : null;
};
const Filters = ({ filterGroupsOrder, filters, selectedFilters, onFilterChange, dictionary, additionalData, title, }) => {
    const [collapsedGroup, setCollapsedGroup] = React.useState(() => buildCollapsedGroup(filterGroupsOrder, filters));
    React.useEffect(() => {
        setCollapsedGroup(previous => buildCollapsedGroup(filterGroupsOrder, filters, previous));
    }, [filterGroupsOrder, filters]);
    const handleGroupItemClick = React.useCallback((groupValue, filterValue) => {
        onFilterChange(groupValue, filterValue);
    }, [onFilterChange]);
    const renderGroup = React.useCallback((groupValue, filter, isCollapsed) => ((0, jsx_runtime_1.jsx)(FilterItems_1.default, { guiType: filter.guiType, groupValue: groupValue, items: filter.items, selectedFilters: selectedFilters, onClick: handleGroupItemClick, isVisible: !isCollapsed })), [handleGroupItemClick, selectedFilters]);
    const renderExpandableFilters = React.useCallback((filter, groupValue) => {
        var _a;
        const isCollapsed = (_a = collapsedGroup[groupValue]) !== null && _a !== void 0 ? _a : false;
        return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)("summary", { className: "py-md pr-2xs -outline-offset-2 group/summary hover:bg-surface-primary-hovered focus-visible:bg-surface-primary-hovered flex list-none items-center justify-between hover:cursor-pointer", children: [(0, jsx_runtime_1.jsx)("span", { className: "px-md", id: "filter-bu-label", children: filter.title }), (0, jsx_runtime_1.jsx)(MosaicIcon_1.MosaicIcon, { name: "chevron-right", className: "df-icon p-2xs group-hover/summary:bg-fill-secondary-hovered group-open/details:bg-fill-secondary-pressed group-focus-visible/summary:bg-fill-secondary-hovered size-5 transition-transform group-open/details:rotate-270 rotate-90 rounded-full", "aria-hidden": "true", focusable: "false" })] }), (0, jsx_runtime_1.jsx)("div", { "aria-labelledby": "filter-mk-label", children: renderGroup(groupValue, filter, isCollapsed) })] }));
    }, [collapsedGroup, renderGroup]);
    const renderDropdownFilters = React.useCallback((filter, groupValue) => {
        var _a, _b;
        const defaultLabel = filter.title;
        const currentValue = (_b = (_a = selectedFilters[groupValue]) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '';
        return ((0, jsx_runtime_1.jsx)(Forms_1.FormDropdown, { name: defaultLabel, value: currentValue, label: filter.label, placeholder: filter.placeholder || defaultLabel, options: filter.items, onChange: event => handleGroupItemClick(groupValue, event.target.value), hiddenDefaultOption: !defaultLabel, cssClass: "form-select--gamma" }));
    }, [handleGroupItemClick, selectedFilters]);
    const renderTextboxFilters = React.useCallback((filter, groupValue) => {
        var _a, _b;
        const defaultLabel = filter.title;
        const selectedValue = (_b = (_a = selectedFilters[groupValue]) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '';
        const [, valueWithoutPrefix] = selectedValue.split(':');
        const normalizedValue = valueWithoutPrefix !== null && valueWithoutPrefix !== void 0 ? valueWithoutPrefix : selectedValue;
        if (filter.typeAlias === FilterTypings_1.FilterGroupTypeAlias.ZipCode) {
            const cultureWithoutZipCodeFilter = 'en-US';
            const selectedCountry = getSelectedFilterByType(filters, selectedFilters, FilterTypings_1.FilterGroupTypeAlias.Countries);
            if (!selectedCountry || (additionalData === null || additionalData === void 0 ? void 0 : additionalData.culture) === cultureWithoutZipCodeFilter) {
                return null;
            }
            return ((0, jsx_runtime_1.jsx)(FilterTextFormField_1.FilterTextField, { value: normalizedValue, placeholder: defaultLabel, onChange: value => {
                    const payload = value ? `${FilterTypings_1.FilterGroupTypeAlias.ZipCode}:${value}` : value;
                    handleGroupItemClick(groupValue, payload);
                }, maxLength: 25 }));
        }
        return ((0, jsx_runtime_1.jsx)(FilterTextFormField_1.FilterTextField, { value: normalizedValue, placeholder: defaultLabel, onChange: value => handleGroupItemClick(groupValue, value), maxLength: 25 }));
    }, [additionalData, filters, handleGroupItemClick, selectedFilters]);
    const renderAutosuggestFilters = React.useCallback((filter, groupValue) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const selectedOption = (_b = (_a = selectedFilters[groupValue]) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '';
        const handleChange = (key) => {
            handleGroupItemClick(groupValue, key);
        };
        switch (filter.typeAlias) {
            case FilterTypings_1.FilterGroupTypeAlias.Countries: {
                const placeholder = dictionary.countryDefaultLabel || filter.title;
                return ((0, jsx_runtime_1.jsx)(FilterAutocomplete_1.FilterAutocomplete, { placeholder: placeholder, onChange: handleChange, items: additionalData === null || additionalData === void 0 ? void 0 : additionalData.countryOptions, selectedOption: selectedOption }));
            }
            case FilterTypings_1.FilterGroupTypeAlias.Region: {
                const hasStates = (_d = (_c = additionalData === null || additionalData === void 0 ? void 0 : additionalData.stateOptions) === null || _c === void 0 ? void 0 : _c.states) === null || _d === void 0 ? void 0 : _d.length;
                if (!hasStates) {
                    return null;
                }
                return ((0, jsx_runtime_1.jsx)(FilterAutocomplete_1.FilterAutocomplete, { placeholder: filter.title, onChange: handleChange, items: (_e = additionalData === null || additionalData === void 0 ? void 0 : additionalData.stateOptions) === null || _e === void 0 ? void 0 : _e.states, selectedOption: selectedOption }));
            }
            case FilterTypings_1.FilterGroupTypeAlias.City: {
                const hasCities = (_g = (_f = additionalData === null || additionalData === void 0 ? void 0 : additionalData.citiesOptions) === null || _f === void 0 ? void 0 : _f.cities) === null || _g === void 0 ? void 0 : _g.length;
                if (!hasCities) {
                    return null;
                }
                const selectedCountry = getSelectedFilterByType(filters, selectedFilters, FilterTypings_1.FilterGroupTypeAlias.Countries);
                const selectedRegion = getSelectedFilterByType(filters, selectedFilters, FilterTypings_1.FilterGroupTypeAlias.Region);
                const requiresRegion = selectedCountry
                    ? CountriesWithStates_1.mappedCountriesWithStates.includes(selectedCountry)
                    : false;
                if (!selectedCountry || (requiresRegion && !selectedRegion)) {
                    return null;
                }
                return ((0, jsx_runtime_1.jsx)(FilterAutocomplete_1.FilterAutocomplete, { placeholder: filter.title, onChange: handleChange, items: (_h = additionalData === null || additionalData === void 0 ? void 0 : additionalData.citiesOptions) === null || _h === void 0 ? void 0 : _h.cities, selectedOption: selectedOption }));
            }
            default:
                console.warn(`There is no such autosuggest: ${filter.typeAlias} \\ ${filter.title}`);
                return null;
        }
    }, [additionalData, dictionary, filters, handleGroupItemClick, selectedFilters]);
    const renderFilters = React.useCallback((groupValue) => {
        var _a, _b;
        const filter = filters[groupValue] || {};
        let renderedFilters = null;
        switch (filter.guiType) {
            case FilterTypings_1.FilterGroupGuiTypeEnum.Autosuggest:
                renderedFilters = renderAutosuggestFilters(filter, groupValue);
                break;
            case FilterTypings_1.FilterGroupGuiTypeEnum.Hierarchy:
            case FilterTypings_1.FilterGroupGuiTypeEnum.Dropdown:
                renderedFilters = renderDropdownFilters(filter, groupValue);
                break;
            case FilterTypings_1.FilterGroupGuiTypeEnum.Textbox:
                renderedFilters = renderTextboxFilters(filter, groupValue);
                break;
            default:
                if (!((_a = filter.items) === null || _a === void 0 ? void 0 : _a.length)) {
                    return null;
                }
                renderedFilters = renderExpandableFilters(filter, groupValue);
        }
        const isCollapsed = (_b = collapsedGroup[groupValue]) !== null && _b !== void 0 ? _b : false;
        return ((0, jsx_runtime_1.jsx)("details", { open: !isCollapsed, className: "border-t-sm border-primary group/details overflow-hidden [interpolate-size:allow-keywords] [&::details-content]:[transition-behavior:allow-discrete_!important] [&::details-content]:[block-size:0] [&::details-content]:[transition:block-size_500ms,content-visibility_500ms] open:[&::details-content]:[block-size:auto]", "data-filter-id": filter.value, children: renderedFilters }, `${filter.title}${filter.value}`));
    }, [
        filters,
        renderAutosuggestFilters,
        renderDropdownFilters,
        renderExpandableFilters,
        renderTextboxFilters,
    ]);
    return ((0, jsx_runtime_1.jsxs)("section", { className: "filters py-2xl px-2xs bg-fill-primary border-primary border-sm flex flex-col rounded-3xl", role: "region", "aria-labelledby": "filters-heading", "data-testid": "filters", children: [title && ((0, jsx_runtime_1.jsx)("h3", { className: "heading-sm px-md mb-md", id: "filters-heading", children: title })), (0, jsx_runtime_1.jsx)("div", { className: "filters-group-list flex flex-col", children: (0, webexUI_1.safe)(filterGroupsOrder).map(groupValue => renderFilters(groupValue)) })] }));
};
exports.default = Filters;
